import { useCallback, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic';
import { AddIcon, CalendarMonthIcon, ListIcon, PersonIcon, ChevronRightIcon } from '@/components/ui/Icons';
import { Button } from '@/components/ui/Button/Button';
import { AboutAppModal } from '@/components/Layout/AboutAppModal';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import { ContextMenuSubmenu } from '@/components/ui/ContextMenu/ContextMenuSubmenu';
import { ContextMenuDivider } from '@/components/ui/ContextMenu/ContextMenuDivider';
import { useAuth } from '@/contexts/useAuth';
import { projectsPathForDesign } from '@/lib/designChoice';
import '@/components/ui/ContextMenu/ContextMenu.css';
import '@/components/ProjectsTable/TableContextMenu.css';
import './NavbarAppMenu.css';

export interface NavbarAppMenuProps {
  onAddProject?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onAddPerson?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onOpenMiniAppPanel?: () => void;
}

export function NavbarAppMenu({
  onAddProject,
  onAddPerson,
  onOpenMiniAppPanel,
}: NavbarAppMenuProps) {
  const { capabilities } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const btnRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [actionsSubAnchor, setActionsSubAnchor] = useState<HTMLElement | null>(null);
  const submenuCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isProjectsRoute = location.pathname === '/' || location.pathname === '/projects';
  const isSchedule = location.pathname === '/schedule';
  const isReadersRoute = location.pathname === '/readers';
  const isStudiosRoute = location.pathname === '/studios';
  const isFonotekaRoute = location.pathname === '/fonoteka';
  const hasActions = Boolean(onAddProject || onAddPerson);
  const showProjects = capabilities.projects.read;
  const showSchedule = capabilities.schedule.read;
  const showReaders = capabilities.readers.read;
  const showStudios = capabilities.studios.read;
  const showFonoteka = capabilities.fonoteka.read;
  const showMiniApps = capabilities.hasAnyGrant && Boolean(onOpenMiniAppPanel);

  const cancelSubmenuClose = useCallback(() => {
    if (submenuCloseTimerRef.current) {
      clearTimeout(submenuCloseTimerRef.current);
      submenuCloseTimerRef.current = null;
    }
  }, []);

  const scheduleSubmenuClose = useCallback(() => {
    cancelSubmenuClose();
    submenuCloseTimerRef.current = setTimeout(() => {
      setActionsSubAnchor(null);
    }, 120);
  }, [cancelSubmenuClose]);

  const closeMenu = useCallback(() => {
    cancelSubmenuClose();
    setMenuOpen(false);
    setActionsSubAnchor(null);
  }, [cancelSubmenuClose]);

  const toggleMenu = useCallback(() => {
    setMenuOpen((o) => !o);
  }, []);

  return (
    <>
      <Button
        ref={btnRef}
        type="button"
        variant="outlined"
        size="s"
        className="navbar__header-icon-btn navbar-app-menu__trigger"
        onClick={toggleMenu}
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        aria-controls="navbar-app-menu"
        title="Меню"
        aria-label="Меню приложения"
      >
        <i className="fa-solid fa-bars" aria-hidden />
      </Button>

      <ContextMenu
        isOpen={menuOpen}
        onClose={closeMenu}
        anchorPosition={null}
        anchorEl={menuOpen ? btnRef.current : null}
        anchorPriorityAxis="y"
        excludeRefs={[btnRef]}
      >
        <ul id="navbar-app-menu" className="context-menu__list table-context-menu__list navbar-app-menu__list" role="menu">
          {showProjects ? (
          <li className="context-menu__list-item" role="none">
            <button
              type="button"
              className={`context-menu__action ${isProjectsRoute ? 'context-menu__action_selected' : ''}`}
              role="menuitem"
              aria-current={isProjectsRoute ? 'page' : undefined}
              onClick={() => {
                navigate(projectsPathForDesign());
                closeMenu();
              }}
            >
              <ListIcon size={18} aria-hidden />
              <span className="context-menu__action-text">Проекты</span>
            </button>
          </li>
          ) : null}
          {showSchedule ? (
          <li className="context-menu__list-item" role="none">
            <button
              type="button"
              className={`context-menu__action ${isSchedule ? 'context-menu__action_selected' : ''}`}
              role="menuitem"
              aria-current={isSchedule ? 'page' : undefined}
              onClick={() => {
                navigate('/schedule');
                closeMenu();
              }}
            >
              <CalendarMonthIcon size={18} aria-hidden />
              <span className="context-menu__action-text">Расписание</span>
            </button>
          </li>
          ) : null}
          {showReaders ? (
          <li className="context-menu__list-item" role="none">
            <button
              type="button"
              className={`context-menu__action ${isReadersRoute ? 'context-menu__action_selected' : ''}`}
              role="menuitem"
              aria-current={isReadersRoute ? 'page' : undefined}
              onClick={() => {
                navigate('/readers');
                closeMenu();
              }}
            >
              <PersonIcon size={18} aria-hidden />
              <span className="context-menu__action-text">Чтецы</span>
            </button>
          </li>
          ) : null}
          {showStudios ? (
          <li className="context-menu__list-item" role="none">
            <button
              type="button"
              className={`context-menu__action ${isStudiosRoute ? 'context-menu__action_selected' : ''}`}
              role="menuitem"
              aria-current={isStudiosRoute ? 'page' : undefined}
              onClick={() => {
                navigate('/studios');
                closeMenu();
              }}
            >
              <i className="fa-solid fa-building navbar-app-menu__fa" aria-hidden />
              <span className="context-menu__action-text">Студии</span>
            </button>
          </li>
          ) : null}
          {showFonoteka ? (
          <li className="context-menu__list-item" role="none">
            <button
              type="button"
              className={`context-menu__action ${isFonotekaRoute ? 'context-menu__action_selected' : ''}`}
              role="menuitem"
              aria-current={isFonotekaRoute ? 'page' : undefined}
              onClick={() => {
                navigate('/fonoteka');
                closeMenu();
              }}
            >
              <LibraryMusicIcon sx={{ fontSize: 18 }} aria-hidden />
              <span className="context-menu__action-text">Фонотека</span>
            </button>
          </li>
          ) : null}

          {hasActions ? (
            <>
              <ContextMenuDivider inList listSpacing />
              <li
                className="context-menu__list-item table-context-menu__item_with-sub"
                role="none"
                onMouseEnter={(e) => {
                  cancelSubmenuClose();
                  setActionsSubAnchor(e.currentTarget);
                }}
                onMouseLeave={() => scheduleSubmenuClose()}
              >
                <button type="button" className="context-menu__action context-menu__action_with-sub">
                  <i className="fa-solid fa-gear navbar-app-menu__fa" aria-hidden />
                  <span className="context-menu__action-text">Действия</span>
                  <ChevronRightIcon size={16} className="context-menu__action-chevron" aria-hidden />
                </button>
                {actionsSubAnchor ? (
                  <ContextMenuSubmenu
                    anchorEl={actionsSubAnchor}
                    onMouseEnter={cancelSubmenuClose}
                    onMouseLeave={() => {
                      setActionsSubAnchor(null);
                    }}
                  >
                    <ul className="context-menu__list" role="menu">
                      {onAddProject ? (
                        <li className="context-menu__list-item" role="none">
                          <button
                            type="button"
                            className="context-menu__action"
                            role="menuitem"
                            onClick={(e) => {
                              onAddProject(e);
                              closeMenu();
                            }}
                          >
                            <AddIcon size={18} aria-hidden />
                            <span className="context-menu__action-text">Добавить проект</span>
                          </button>
                        </li>
                      ) : null}
                      {onAddPerson ? (
                        <li className="context-menu__list-item" role="none">
                          <button
                            type="button"
                            className="context-menu__action"
                            role="menuitem"
                            onClick={(e) => {
                              onAddPerson(e);
                              closeMenu();
                            }}
                          >
                            <PersonIcon size={18} aria-hidden />
                            <span className="context-menu__action-text">Управление каталогом</span>
                          </button>
                        </li>
                      ) : null}
                    </ul>
                  </ContextMenuSubmenu>
                ) : null}
              </li>
            </>
          ) : null}

          <ContextMenuDivider inList listSpacing />
          {showMiniApps ? (
            <li className="context-menu__list-item" role="none">
              <button
                type="button"
                className="context-menu__action"
                role="menuitem"
                onClick={() => {
                  onOpenMiniAppPanel?.();
                  closeMenu();
                }}
              >
                <i className="fa-solid fa-puzzle-piece navbar-app-menu__fa" aria-hidden />
                <span className="context-menu__action-text">Мини-приложения</span>
              </button>
            </li>
          ) : null}
          <li className="context-menu__list-item" role="none">
            <button
              type="button"
              className="context-menu__action"
              role="menuitem"
              onClick={() => {
                setAboutOpen(true);
                closeMenu();
              }}
            >
              <i className="fa-solid fa-circle-info navbar-app-menu__fa" aria-hidden />
              <span className="context-menu__action-text">О приложении</span>
            </button>
          </li>
        </ul>
      </ContextMenu>
      <AboutAppModal open={aboutOpen} onClose={() => setAboutOpen(false)} />
    </>
  );
}
